import { CodedError, messageFor, russianMessage, responseMessage } from './messages';
export class HttpError extends CodedError {
    constructor(status, message, data) {
        const info = messageFor(status, data);
        super(info.message_id, russianMessage(message, info.message_texts.ru));
        this.message_texts = info.message_texts;
        this.status = status;
        this.data = data; // ✅ сохраняем тело
    }
}
export class RequestTimeoutError extends CodedError {
    constructor(message = "Сервер не ответил вовремя. Проверьте интернет-соединение и повторите попытку.") {
        super(2000, message);
        this.name = "RequestTimeoutError";
    }
}
export class UnsafeRequestUrlError extends CodedError {
    constructor(message = "Запрос к внешнему адресу заблокирован") {
        super(2003, message);
        this.name = "UnsafeRequestUrlError";
    }
}
let unauthorizedHandler = null;
export const setUnauthorizedHandler = (handler) => {
    unauthorizedHandler = handler;
    return () => {
        if (unauthorizedHandler === handler)
            unauthorizedHandler = null;
    };
};
export const handleUnauthorizedStatus = (status, token) => {
    if (status === 401 && token && unauthorizedHandler) {
        void Promise.resolve(unauthorizedHandler(token)).catch(() => { });
    }
};
const redirectToLoginIfUnauthorized = () => {
    if (typeof window === "undefined")
        return;
    try {
        localStorage.removeItem("accessToken");
    }
    catch { }
    const { pathname, search, hash } = window.location;
    const isAuthPage = pathname === "/userlogin" ||
        pathname === "/register" ||
        pathname === "/createuser" ||
        pathname === "/passwordreset" ||
        pathname.startsWith("/reset-password/") ||
        pathname.startsWith("/auth/vk/callback");
    if (isAuthPage)
        return;
    const next = `${pathname}${search}${hash}`;
    window.location.replace(`/userlogin?next=${encodeURIComponent(next)}`);
};
const DEFAULT_REQUEST_TIMEOUT_MS = 60000;
const UPLOAD_REQUEST_TIMEOUT_MS = 5 * 60000;
// Fetch can resolve its headers while the response body remains stalled.
export async function readResponseTextWithTimeout(response, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS) {
    let timer;
    try {
        return await Promise.race([
            response.text(),
            new Promise((_resolve, reject) => {
                timer = setTimeout(() => reject(new RequestTimeoutError()), timeoutMs);
            }),
        ]);
    }
    finally {
        if (timer)
            clearTimeout(timer);
    }
}
export const fetchWithTimeout = async (input, init, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS) => {
    const controller = new AbortController();
    const externalSignal = init.signal;
    let timedOut = false;
    const abortFromExternal = () => controller.abort(externalSignal === null || externalSignal === void 0 ? void 0 : externalSignal.reason);
    if (externalSignal === null || externalSignal === void 0 ? void 0 : externalSignal.aborted)
        abortFromExternal();
    else
        externalSignal === null || externalSignal === void 0 ? void 0 : externalSignal.addEventListener("abort", abortFromExternal, { once: true });
    let timer;
    const deadline = new Promise((_resolve, reject) => {
        timer = setTimeout(() => {
            timedOut = true;
            controller.abort();
            reject(new RequestTimeoutError());
        }, timeoutMs);
    });
    try {
        return await Promise.race([fetch(input, { ...init, signal: controller.signal }), deadline]);
    }
    catch (error) {
        if (timedOut)
            throw new RequestTimeoutError();
        if (externalSignal === null || externalSignal === void 0 ? void 0 : externalSignal.aborted)
            throw new CodedError(2002);
        if (error instanceof TypeError)
            throw new CodedError(2001);
        throw error;
    }
    finally {
        clearTimeout(timer);
        externalSignal === null || externalSignal === void 0 ? void 0 : externalSignal.removeEventListener("abort", abortFromExternal);
    }
};
const BACKGROUND_TASK_POLL_INTERVAL_MS = 5000;
const PHOTO_TASK_OPERATIONS = new Set(['travel_plan_images', 'description_images', 'user_avatar']);
const BACKGROUND_TASK_TIMEOUT_MS = 15 * 60 * 1000;
const BACKGROUND_TASK_STATUS_MAX_RETRIES = 3;
const TERMINAL_FAILURE_STATUSES = new Set(["FAILURE", "TIMEOUT"]);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const resolveTrustedUrl = (baseUrl, path) => {
    const base = new URL(baseUrl);
    const resolved = new URL(path, `${base.toString().replace(/\/$/, "")}/`);
    if (resolved.protocol !== base.protocol || resolved.origin !== base.origin) {
        throw new UnsafeRequestUrlError();
    }
    return resolved.toString();
};
const getPayloadMessage = (payload, fallback) => {
    var _a;
    if (typeof ((_a = payload === null || payload === void 0 ? void 0 : payload.error) === null || _a === void 0 ? void 0 : _a.message) === "string" && payload.error.message) {
        return payload.error.message;
    }
    if (typeof (payload === null || payload === void 0 ? void 0 : payload.error) === "string" && payload.error)
        return payload.error;
    if (typeof (payload === null || payload === void 0 ? void 0 : payload.message) === "string" && payload.message)
        return payload.message;
    if (typeof (payload === null || payload === void 0 ? void 0 : payload.detail) === "string" && payload.detail)
        return payload.detail;
    return fallback;
};
const collectStatusUrls = (payload) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const urls = [];
    const add = (value) => {
        if (typeof value !== "string")
            return;
        const normalized = value.trim();
        if (normalized)
            urls.push(normalized);
    };
    const addTasks = (tasks) => {
        if (!Array.isArray(tasks))
            return;
        tasks.forEach((task) => { var _a; return add((_a = task === null || task === void 0 ? void 0 : task.status_url) !== null && _a !== void 0 ? _a : task === null || task === void 0 ? void 0 : task.statusUrl); });
    };
    add((_a = payload === null || payload === void 0 ? void 0 : payload.status_url) !== null && _a !== void 0 ? _a : payload === null || payload === void 0 ? void 0 : payload.statusUrl);
    add((_c = (_b = payload === null || payload === void 0 ? void 0 : payload.result) === null || _b === void 0 ? void 0 : _b.status_url) !== null && _c !== void 0 ? _c : (_d = payload === null || payload === void 0 ? void 0 : payload.result) === null || _d === void 0 ? void 0 : _d.statusUrl);
    add((_f = (_e = payload === null || payload === void 0 ? void 0 : payload.data) === null || _e === void 0 ? void 0 : _e.status_url) !== null && _f !== void 0 ? _f : (_g = payload === null || payload === void 0 ? void 0 : payload.data) === null || _g === void 0 ? void 0 : _g.statusUrl);
    addTasks(payload === null || payload === void 0 ? void 0 : payload.tasks);
    addTasks((_h = payload === null || payload === void 0 ? void 0 : payload.result) === null || _h === void 0 ? void 0 : _h.tasks);
    addTasks((_j = payload === null || payload === void 0 ? void 0 : payload.data) === null || _j === void 0 ? void 0 : _j.tasks);
    return Array.from(new Set(urls));
};
const waitForBackgroundStatus = async ({ statusUrl, baseUrl, token, signal, }) => {
    const timeoutAt = Date.now() + BACKGROUND_TASK_TIMEOUT_MS;
    let consecutiveErrors = 0;
    let pollIntervalMs = BACKGROUND_TASK_POLL_INTERVAL_MS;
    while (Date.now() < timeoutAt) {
        const response = await fetchWithTimeout(resolveTrustedUrl(baseUrl, statusUrl), {
            method: "GET",
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            credentials: "include",
            signal,
        });
        if (!response.ok) {
            handleUnauthorizedStatus(response.status, token);
            let errorPayload;
            try {
                errorPayload = await response.json();
            }
            catch { }
            const error = new HttpError(response.status, getPayloadMessage(errorPayload, "Не удалось получить статус фоновой задачи"), errorPayload);
            // Сразу после ответа 202 запись задачи может стать доступна с небольшой
            // задержкой. Кратковременно повторяем 404/5xx, не завершая loader.
            if (response.status === 404 || response.status >= 500) {
                consecutiveErrors += 1;
                if (consecutiveErrors <= BACKGROUND_TASK_STATUS_MAX_RETRIES) {
                    await sleep(pollIntervalMs);
                    continue;
                }
            }
            throw error;
        }
        consecutiveErrors = 0;
        let task;
        try {
            task = await response.json();
        }
        catch {
            throw new CodedError(2004);
        }
        pollIntervalMs = PHOTO_TASK_OPERATIONS.has(task === null || task === void 0 ? void 0 : task.operation) ? 2000 : BACKGROUND_TASK_POLL_INTERVAL_MS;
        const status = String((task === null || task === void 0 ? void 0 : task.status) || "").toUpperCase();
        if (status === "SUCCESS")
            return task;
        if (TERMINAL_FAILURE_STATUSES.has(status)) {
            throw new CodedError(status === 'TIMEOUT' ? 3004 : 3003, russianMessage(getPayloadMessage(task, "Фоновая операция завершилась с ошибкой"), messageFor(200, task).message_texts.ru));
        }
        await sleep(pollIntervalMs);
    }
    throw new CodedError(3004);
};
const mergeBackgroundResults = (queuedPayload, completedTasks) => {
    const results = completedTasks.map((task) => task === null || task === void 0 ? void 0 : task.result);
    const singleResult = results.length === 1 ? results[0] : undefined;
    const resultFields = singleResult && typeof singleResult === "object" && !Array.isArray(singleResult)
        ? singleResult
        : {};
    return {
        ...queuedPayload,
        ...resultFields,
        ...responseMessage(200, { ...resultFields, api_message: undefined, status: 'SUCCESS' }),
        success: (resultFields === null || resultFields === void 0 ? void 0 : resultFields.success) === false ? false : true,
        status: "SUCCESS",
        terminal: true,
        task_id: undefined,
        status_url: undefined,
        tasks: undefined,
        result: results.length === 1 ? singleResult : results,
        task_results: results,
    };
};
const DICTIONARY_CACHE_TTL_MS = 5 * 60000;
const DICTIONARY_CACHE_LIMIT = 32;
const CACHED_DICTIONARY_PATHS = new Set([
    '/api/dict_countries/', '/api/dict_travel_types/', '/api/dict_currencies/',
    '/api/dict_travel_tariff_types/', '/api/dict_travel_status_private/', '/api/dict_travel_status_public/',
    '/api/dict_travel_items/', '/api/dict_travel_items_from_point_table/', '/api/dict_gpx_type/',
]);
const dictionaryRequests = new Map();
export async function apiFetch(path, opts) {
    // Local mobile requests are resolved before apiFetch. Cache only authenticated,
    // read-only cloud dictionaries; cancellation and explicit reloads stay independent.
    if (!opts.token || (opts.method || 'GET').toUpperCase() !== 'GET' || opts.body != null
        || opts.signal || opts.requestTimeoutMs !== undefined || (opts.cache && opts.cache !== 'default')) {
        return requestApi(path, opts);
    }
    const url = resolveTrustedUrl(opts.baseUrl, path);
    if (!CACHED_DICTIONARY_PATHS.has(new URL(url).pathname))
        return requestApi(path, opts);
    const key = JSON.stringify([url, opts.token, Object.entries(opts.headers || {}).sort(),
        opts.redirectOnUnauthorized, opts.notifyOnUnauthorized, opts.waitForBackgroundTask]);
    let entry = dictionaryRequests.get(key);
    if (entry && entry.expiresAt <= Date.now()) {
        dictionaryRequests.delete(key);
        entry = undefined;
    }
    if (!entry) {
        const next = { expiresAt: Infinity, promise: Promise.resolve(undefined) };
        const discard = () => { if (dictionaryRequests.get(key) === next)
            dictionaryRequests.delete(key); };
        next.promise = requestApi(path, opts).then(value => {
            const result = value;
            if (result && typeof result === 'object' && result.success !== false && !result.error && !result.errors) {
                next.expiresAt = Date.now() + DICTIONARY_CACHE_TTL_MS;
            }
            else
                discard();
            return value;
        }, error => { discard(); throw error; });
        dictionaryRequests.set(key, next);
        while (dictionaryRequests.size > DICTIONARY_CACHE_LIMIT)
            dictionaryRequests.delete(dictionaryRequests.keys().next().value);
        entry = next;
    }
    const value = await entry.promise;
    // Dictionary consumers may sort or enrich rows; each caller receives its own copy.
    return (value === undefined ? value : JSON.parse(JSON.stringify(value)));
}
async function requestApi(path, opts) {
    const { baseUrl, token, headers, redirectOnUnauthorized = true, notifyOnUnauthorized = true, waitForBackgroundTask = true, requestTimeoutMs = typeof FormData !== "undefined" && opts.body instanceof FormData
        ? UPLOAD_REQUEST_TIMEOUT_MS : DEFAULT_REQUEST_TIMEOUT_MS, signal, onResponse, ...rest } = opts;
    const res = await fetchWithTimeout(resolveTrustedUrl(baseUrl, path), {
        ...rest,
        signal,
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(headers !== null && headers !== void 0 ? headers : {})
        },
        credentials: "include"
    }, requestTimeoutMs);
    onResponse === null || onResponse === void 0 ? void 0 : onResponse();
    if (!res.ok) {
        if (notifyOnUnauthorized)
            handleUnauthorizedStatus(res.status, token);
        if (res.status === 401 && redirectOnUnauthorized && !(token && notifyOnUnauthorized && unauthorizedHandler)) {
            redirectToLoginIfUnauthorized();
        }
        let message = `HTTP error: ${res.status}`;
        let payload = undefined;
        try {
            const text = await readResponseTextWithTimeout(res, requestTimeoutMs);
            if (text) {
                try {
                    payload = JSON.parse(text);
                    message =
                        (typeof (payload === null || payload === void 0 ? void 0 : payload.message) === "string" && payload.message) ||
                            (typeof (payload === null || payload === void 0 ? void 0 : payload.error) === "string" && payload.error) ||
                            (typeof (payload === null || payload === void 0 ? void 0 : payload.detail) === "string" && payload.detail) ||
                            ((payload === null || payload === void 0 ? void 0 : payload.errors) && typeof payload.errors === "object"
                                ? Object.values(payload.errors).join(", ")
                                : "") ||
                            message;
                }
                catch {
                    message = text.slice(0, 300) || message;
                }
            }
        }
        catch { }
        throw new HttpError(res.status, message, payload); // ✅ вот ключ
    }
    // --- success branch ---
    // 204 No Content или реально пустое тело → не пытаемся json()
    if (res.status === 204) {
        return undefined;
    }
    // иногда бэк может вернуть 200, но пустое тело
    const text = await readResponseTextWithTimeout(res, requestTimeoutMs);
    if (!text) {
        return undefined;
    }
    // обычный JSON ответ
    let payload;
    try {
        payload = JSON.parse(text);
    }
    catch {
        throw new CodedError(2004);
    }
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        if (payload.api_message == null)
            Object.assign(payload, responseMessage(res.status, payload));
    }
    if (waitForBackgroundTask) {
        const statusUrls = collectStatusUrls(payload);
        const payloadStatus = String((payload === null || payload === void 0 ? void 0 : payload.status) || "").toUpperCase();
        if (TERMINAL_FAILURE_STATUSES.has(payloadStatus)) {
            const info = messageFor(res.status, payload);
            throw new CodedError(info.message_id, russianMessage(getPayloadMessage(payload, info.message_texts.ru), info.message_texts.ru));
        }
        const isTerminalPayload = (payload === null || payload === void 0 ? void 0 : payload.terminal) === true ||
            payloadStatus === "SUCCESS" ||
            TERMINAL_FAILURE_STATUSES.has(payloadStatus);
        if (statusUrls.length > 0 && !isTerminalPayload) {
            const completedTasks = await Promise.all(statusUrls.map((statusUrl) => waitForBackgroundStatus({
                statusUrl,
                baseUrl,
                token,
                signal,
            })));
            return mergeBackgroundResults(payload, completedTasks);
        }
        if (res.status === 202 && statusUrls.length === 0) {
            throw new CodedError(2004, "Сервер поставил операцию в очередь, но не вернул status_url");
        }
    }
    return payload;
}
